import findIndex from 'lodash/findIndex';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import {window} from 'ssr-window';
import DOMUtils from '@spothero/utils/dom';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import SearchUtils from 'search/search-utils';
import SHLoader from 'common/loader/SHLoader';
import {getPriceRange} from 'filters/filters-selectors';
import {SEARCH_TYPE} from './search-prop-types';
import WithSearch from './WithSearch';
import SearchControls from './controls/SearchControls';
import SpotListContainerDesktop from './spot-list/SpotListContainerDesktop';

const SpotDetailsModalDesktop = loadable(() =>
    import('spot-details/SpotDetailsModalDesktop')
);
const SearchMap = loadable(() => import('./map/SearchMap'));
const CityInfoPanelDesktop = loadable(() =>
    import('city/CityInfoPanelDesktop')
);
const DestinationInfoPanelDesktop = loadable(() =>
    import('destination/DestinationInfoPanelDesktop')
);
const DestinationInfoPanel = loadable(() =>
    import('destination/DestinationInfoPanel')
);

class SearchDesktop extends Component {
    static propTypes = {
        isMonthly: PropTypes.bool,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpot: PropTypes.object,
        monthlyPhoneNumber: PropTypes.string.isRequired,
        monthlyPhoneNumberFriendly: PropTypes.string.isRequired,
        searchType: SEARCH_TYPE.isRequired,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        lowPrice: PropTypes.number.isRequired,
        highPrice: PropTypes.number.isRequired,
        searchRequestId: PropTypes.string,
        isInfoPanelVisible: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        showSpotDetailsModal: PropTypes.bool,
        pushTo: PropTypes.func,
        findParkingNearMe: PropTypes.func,
        onToggleEventList: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func,
        isPolishTimepickersEnabled: PropTypes.bool,
    };
    static defaultProps = {
        isInfoPanelVisible: true,
    };
    state = {
        minimizeSearchDetails: false,
    };

    constructor(props) {
        super(props);

        const {searchType, city, destination, event} = props;

        this._hasInfoPanel = SearchUtils.hasInfoPanel({
            searchType,
            city,
            destination,
            event,
        });
    }

    componentDidMount() {
        this._spotListEl = DOMUtils.el('.SpotListContainer .list-scroll');
        this._searchControlsEl = DOMUtils.el('.SpotListSearchControls');

        window.addEventListener('keydown', this._onKeyDown);

        setTimeout(() => {
            this._toggleInfoPanel();
        }, 0);
    }

    componentDidUpdate(nextProps) {
        const {
            searchType,
            city,
            destination,
            event,
            showSpotDetailsModal,
            isInfoPanelVisible,
        } = this.props;
        const {
            searchType: nextSearchType,
            showSpotDetailsModal: nextShowSpotDetailsModal,
            isInfoPanelVisible: nextIsInfoPanelVisible,
        } = nextProps;

        if (searchType !== nextSearchType) {
            this._hasInfoPanel = SearchUtils.hasInfoPanel({
                nextSearchType,
                city,
                destination,
                event,
            });

            this._toggleInfoPanel();
        }

        if (
            showSpotDetailsModal !== nextShowSpotDetailsModal ||
            isInfoPanelVisible !== nextIsInfoPanelVisible
        ) {
            this._toggleInfoPanel();
        }
    }

    componentWillUnmount() {
        this._removeInfoPanelClass();

        window.removeEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown = evt => {
        const {spots, selectedSpot, pushTo} = this.props;

        if (!selectedSpot) {
            return;
        }

        const index = findIndex(spots, ['spotId', selectedSpot.spotId]);
        let nextSpot;

        if (evt.which === 40) {
            evt.preventDefault();

            nextSpot = spots[index + 1];
        } else if (evt.which === 38) {
            evt.preventDefault();

            nextSpot = spots[index - 1];
        }

        if (nextSpot) {
            pushTo(`/spot-details/${nextSpot.spotId}`, {referrer: 'list'});
        }
    };

    _onListScroll = () => {
        this.setState({
            minimizeSearchDetails:
                this._spotListEl.scrollTop >
                this._searchControlsEl.offsetHeight,
        });
    };

    _toggleInfoPanel = () => {
        const {
            searchType,
            isInfoPanelVisible,
            showSpotDetailsModal,
        } = this.props;

        this._pageContainer = DOMUtils.el('.search-page');

        if (
            !showSpotDetailsModal &&
            this._hasInfoPanel &&
            isInfoPanelVisible &&
            searchType !== 'address'
        ) {
            this._removeInfoPanelClass();
        } else {
            DOMUtils.addClass(this._pageContainer, 'info-panel-hidden');
        }
    };

    _removeInfoPanelClass() {
        if (this._pageContainer) {
            DOMUtils.removeClass(this._pageContainer, 'info-panel-hidden');
        }
    }

    render() {
        const {
            isMonthly,
            isInfoPanelVisible,
            searchRequestId,
            spots,
            selectedSpot,
            findParkingNearMe,
            onSpotDetailsHidden,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            searchType,
            city,
            destination,
            event,
            lowPrice,
            highPrice,
            showSpotDetailsModal,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
            isPolishTimepickersEnabled,
        } = this.props;
        const {minimizeSearchDetails} = this.state;
        const classes = classNames(
            'Search',
            {'Search-monthly': isMonthly},
            {'Search-polish-experiment': isPolishTimepickersEnabled},
            {'Search-minimize-details': minimizeSearchDetails}
        );
        const showInfoPanel =
            !showSpotDetailsModal && this._hasInfoPanel && isInfoPanelVisible;

        return (
            <div className={classes}>
                <Fragment>
                    {searchRequestId ? (
                        <Fragment>
                            {!isPolishTimepickersEnabled && (
                                <SearchControls
                                    key={searchRequestId}
                                    showActions
                                />
                            )}
                            <div className="search-results">
                                <div className="SearchMap-container">
                                    <SearchMap
                                        spots={spots}
                                        findParkingNearMe={findParkingNearMe}
                                        monthlyPhoneNumber={monthlyPhoneNumber}
                                        monthlyPhoneNumberFriendly={
                                            monthlyPhoneNumberFriendly
                                        }
                                        isPolishTimepickersEnabled={
                                            isPolishTimepickersEnabled
                                        }
                                    />
                                    {showSpotDetailsModal && (
                                        <SpotDetailsModalDesktop
                                            spot={selectedSpot}
                                            onHidden={onSpotDetailsHidden}
                                            event={event}
                                        />
                                    )}
                                </div>
                                <SpotListContainerDesktop
                                    spots={spots}
                                    isEventListEnabled={isEventListEnabled}
                                    showEventList={showEventList}
                                    onToggleEventList={onToggleEventList}
                                    isPolishTimepickersEnabled={
                                        isPolishTimepickersEnabled
                                    }
                                    onListScroll={this._onListScroll}
                                />
                                {showInfoPanel &&
                                    !isPolishTimepickersEnabled &&
                                    searchType !== 'city' &&
                                    searchType !== 'address' && (
                                        <DestinationInfoPanelDesktop
                                            searchType={searchType}
                                            destination={destination}
                                            event={event}
                                            lowPrice={lowPrice}
                                            highPrice={highPrice}
                                            isMonthly={isMonthly}
                                        />
                                    )}
                                {showInfoPanel &&
                                    !isPolishTimepickersEnabled &&
                                    searchType === 'city' && (
                                        <CityInfoPanelDesktop
                                            searchType={searchType}
                                            city={city}
                                            lowPrice={lowPrice}
                                            highPrice={highPrice}
                                            isMonthly={isMonthly}
                                        />
                                    )}
                                {showInfoPanel &&
                                    isPolishTimepickersEnabled &&
                                    searchType !== 'address' && (
                                        <DestinationInfoPanel />
                                    )}
                            </div>
                        </Fragment>
                    ) : (
                        <SHLoader />
                    )}
                </Fragment>
            </div>
        );
    }
}

const makeMapStateToProps = () => state => {
    const {
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        search: {
            data: {isInfoPanelVisible, searchType},
        },
        searchRequest: {id: searchRequestId},
        spot: {showSpotDetailsModal},
    } = state;
    const {priceLow: lowPrice, priceHigh: highPrice} = getPriceRange(state);
    const {
        monthly_phone_number: monthlyPhoneNumber,
        monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
    } = city;

    return {
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        event,
        city,
        destination,
        searchType,
        isInfoPanelVisible,
        showSpotDetailsModal,
        lowPrice,
        highPrice,
        searchRequestId,
    };
};

const enhance = compose(
    WithSearch,
    connect(makeMapStateToProps)
);

export default enhance(SearchDesktop);
